import {createSelector} from '@reduxjs/toolkit'

import {APIStatus, NaverPointStatus, PAYMONEY_RESULT_TYPE, PayMoneyUserStatus} from '@web/core/constants'
import {chargePointSelector, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {orderEntryInfoSelector, pageSetInfoSelector} from '@web/slices/common/slice'
import {orderAmountInfoSelector} from '@web/slices/orderTotal/slice'
import {payMethodAvailableSelector} from '@web/slices/paymentMethod/slice'
import {naverPayPointSelector} from '@web/slices/pointPayment/slice'
import {rewardPointApiStatusSelector, rewardPointSelector} from '@web/slices/rewardPoint/slice'
import {ViewType} from '@web/ui/pointPayment'

import {PointMoneyDisableCase} from './constants'

/** 네이버 포인트 info selector */
export const naverPayPointInfoSelector = createSelector(
    orderAmountInfoSelector,
    naverPayPointSelector,
    ({orderAmount}, naverPayPoint) => {
        const isNaverPointAvailable = naverPayPoint.status === NaverPointStatus.ENABLE
        const possibleMaxUsingAmount = isNaverPointAvailable ? Math.min(orderAmount, naverPayPoint.amount) : 0
        const isUsingAll = naverPayPoint.usingAmount >= naverPayPoint.amount
        return {
            naverPayPoint,
            isNeedUpdate: (function () {
                if (naverPayPoint.usingAmount > 0) {
                    return (
                        // 포인트 사용불가
                        !isNaverPointAvailable ||
                        // 네이버 포인트를 사용하기에 남은 금액이 없습니다.
                        orderAmount <= 0 ||
                        // 네이버 포인트 가능 금액보다 많은 금액을 사용하려고 합니다.
                        naverPayPoint.amount < naverPayPoint.usingAmount ||
                        // 마이스 금액은 사용할 수 없습니다.
                        naverPayPoint.usingAmount < 0
                    )
                }
                return false
            })(),
            /** 전액 사용 */
            isUsingAll,
            /**
             * 사용가능한 최대 포인트
             * - 보유 포인트보다 남은 결제 금액 적다면, 남은 결제 금액을 적용
             * - 보유 포인트보다 남은 결제 금액 많다면, 전체 보유 포인트 적용
             **/
            possibleMaxUsingAmount,
            /**
             * `포인트 전액사용` 기능 사용가능 여부
             * - 보유 포인트 (적포+충포)가 0보다 큰 경우
             * - 사용가능한 최대 포인트가 사용하기 위해 입력한 포인트보다 큰 경우
             **/
            canUseAllPoints: naverPayPoint.amount > 0 && naverPayPoint.usingAmount < possibleMaxUsingAmount,
        }
    },
)

/** 포인트영역 표시 View Type 을 반환
 * view > SIMPLE : 간소화된 UI 적용 (주문형 주문서)
 * view > DEFAULT : 간소화된 UI 미적용
 */
export const pointPaymentViewInfoSelector = createSelector(
    pageSetInfoSelector,
    orderEntryInfoSelector,
    rewardPointSelector,
    ({isDiconOnce}, {isCheckout, isEasyBookingGroup, isSellerGroup, isFamilyPay}, {amount}): ViewType =>
        (isCheckout || isEasyBookingGroup || isSellerGroup || isDiconOnce || isFamilyPay) &&
        // 적포가 마이너스 이면 DEFAULT 노출
        amount >= 0
            ? 'SIMPLE'
            : 'DEFAULT',
)

/**
 * 포인트 조회/점검상태 selector
 */
export const pointStatusSelector = createSelector(
    rewardPointApiStatusSelector,
    rewardPointSelector,
    (rewardPointApiStatus, {maintenanceInfo: isPointInMaintenance}) => {
        const isPointPaymentServiceRejected = rewardPointApiStatus === APIStatus.REJECTED && !!isPointInMaintenance
        const isFailedToGetPointAmount = rewardPointApiStatus === APIStatus.DISABLED

        return {
            // 포인트 서킷 점검 (결제형 서비스에서 점검 처리)
            isPointPaymentServiceRejected,
            // 포인트 조회 실패
            isFailedToGetPointAmount,
        }
    },
)

/**
 * 머니(충전포인트) 조회/점검상태 selector
 */
export const moneyStatusSelector = createSelector(
    payMoneyInfoSelector,
    chargePointSelector,
    ({payMoneyUserResultType}, {status, payMoney}) => {
        const isFailedToGetMoneyAmount = status === APIStatus.REJECTED

        return {
            // 머니 데이터 전체 조회 실패
            isFailedToGetMoneyInfo: payMoney.apiResult === PAYMONEY_RESULT_TYPE.FAILED_RETRY_AVAILABLE,
            // 머니 서킷 점검 (결제형 서비스에서 점검 처리)
            isMoneyPaymentServiceRejected: payMoneyUserResultType === 'FAILED',
            // 머니 잔액 조회 실패
            isFailedToGetMoneyAmount,
        }
    },
)

/**
 * 서비스 설정에 따른 포인트/머니 (페이머니 가입자, 머니온리 가맹점에게는 포인트. 그 외는 포인트/머니) 사용가능 상태 selector
 */
export const pointMoneyStatusSelector = createSelector(
    payMethodAvailableSelector,
    payMoneyInfoSelector,
    pointStatusSelector,
    moneyStatusSelector,
    (
        {isRewardPointAvailable: isPointAvailable, isChargePointAvailable: isMoneyAvailable},
        {isPayMoneyUser, payMoneyUserResultType, payMoneyUserStatus},
        {isPointPaymentServiceRejected},
        {isMoneyPaymentServiceRejected},
    ) => {
        const isMoneyOnly = !isPointAvailable && isMoneyAvailable
        const pointAndMoneyNotAvailable = !isPointAvailable && !isMoneyAvailable
        const isPayMoneyInfoFailed = payMoneyUserResultType === PAYMONEY_RESULT_TYPE.FAILED_RETRY_AVAILABLE
        const isTransferFailed = payMoneyUserStatus === PayMoneyUserStatus.TRANSFER_FAIL
        const isPaymentServiceRejected =
            isMoneyOnly || isPayMoneyUser
                ? isPointPaymentServiceRejected
                : isPointPaymentServiceRejected && isMoneyPaymentServiceRejected

        return {
            isMoneyOnly,
            isPayMoneyUser,
            pointAndMoneyNotAvailable,
            isPayMoneyInfoFailed,
            isTransferFailed,
            isPaymentServiceRejected,
        }
    },
)

/**
 * 포인트/머니 영역 사용불가 여부 리턴하는 selector
 */
export const pointMoneyDisabledSelector = createSelector(
    pointMoneyStatusSelector,
    moneyStatusSelector,
    pointStatusSelector,
    (
        {pointAndMoneyNotAvailable, isMoneyOnly, isPayMoneyUser, isPaymentServiceRejected, isTransferFailed},
        {isFailedToGetMoneyInfo, isMoneyPaymentServiceRejected, isFailedToGetMoneyAmount},
        {isFailedToGetPointAmount},
    ) => {
        return {
            disabledReason: (() => {
                /**
                 * 포인트 머니 사용 불가 (가맹점 미지원)
                 */
                if (pointAndMoneyNotAvailable) {
                    return PointMoneyDisableCase.POINT_MONEY_NOT_AVAILABLE
                }

                if (isMoneyOnly && isPayMoneyUser) {
                    return PointMoneyDisableCase.MONEY_ONLY_MONEY_NOT_AVAILABLE
                }

                /**
                 * 포인트 머니 오류
                 */
                if (isPaymentServiceRejected) {
                    return PointMoneyDisableCase.PAYMENT_SERVICE_REJECTED
                }

                if (isMoneyOnly && isMoneyPaymentServiceRejected) {
                    return PointMoneyDisableCase.MONEY_ONLY_MONEY_PAYMENT_SERVICE_REJECTED
                }

                const isMoneyInfoLoadFailed = isFailedToGetMoneyInfo || isFailedToGetMoneyAmount
                if (
                    (!isPayMoneyUser && isFailedToGetPointAmount && isMoneyInfoLoadFailed) ||
                    (isPayMoneyUser && isFailedToGetPointAmount) ||
                    (isMoneyOnly && isMoneyInfoLoadFailed)
                ) {
                    return PointMoneyDisableCase.FAILED_TO_GET_POINT_MONEY_AMOUNT
                }

                if (isMoneyOnly && isTransferFailed) {
                    return PointMoneyDisableCase.MONEY_ONLY_TRANSFER_FAILED
                }

                return null
            })(),
        }
    },
)
